<script lang="ts" setup>
import type { PriceListItem } from '~/composables/api/price-list/useFetchPriceList'

const props = defineProps<{
  item: PriceListItem
}>()

const emit = defineEmits<{
  contactRequested: []
}>()

const { t } = useI18n()
const { formatCurrency } = useIntl()
const { data: config } = useQueryConfig.current()

const {
  paymentPeriodUnit,
  selectedPlan,
  promotedPlan,
  planSelected,
  priceList,
} = useSubscriptionProcessContext()

const isPriceListV1_1 = computed(() => priceList.dataWithoutUpgrades.value?.data_version === 'v1.1')

const itemPrice = computed(() => props.item.prices.find(({ period_unit }) => period_unit === paymentPeriodUnit.value))

const itemCustomPrice = computed(() => props.item.data.custom_monthly_price)

const itemPriceDivided = computed(() => (itemCustomPrice.value ?? itemPrice.value?.monthly_price ?? 0) / 100)

const itemPriceFormatted = computed(() =>
  formatCurrency(itemPriceDivided.value, { minimumFractionDigits: 0, maximumFractionDigits: 0 }))

const registerPlanSelectedEvent = () => {
  if (!itemPrice.value)
    return

  planSelected({
    plan: props.item.title,
    price: (itemPrice.value.monthly_price * 12) / 100,
    priceId: itemPrice.value.id,
    currency: config.value?.currency_code,
  })
}
</script>

<template>
  <th
    scope="col"
    class="h-full"
  >
    <div class="flex h-full flex-col items-center gap-2.5">
      <div class="flex flex-col gap-2">
        <span
          v-if="itemCustomPrice"
          class="text-xs font-normal text-gray-500"
        >{{ t('subscribe.plans.table.price-from') }}</span>

        <span>
          <span class="text-center text-lg font-semibold">{{ item.title }}</span>
          <DTTooltip
            v-if="item.id === promotedPlan?.id && promotedPlan.info"
            :content="promotedPlan.info"
            tooltip-class="!z-[99]"
          >
            <DTBadge
              class="ml-2 max-w-fit align-text-top text-3xs font-normal"
              variant="green"
            >
              {{ promotedPlan.text }}
            </DTBadge>
          </DTTooltip>

          <DTBadge
            v-else-if="item.id === promotedPlan?.id"
            class="ml-2 max-w-fit align-text-top text-3xs font-normal"
            variant="green"
          >
            {{ promotedPlan.text }}
          </DTBadge>
        </span>
      </div>

      <div
        v-if="(itemPrice?.monthly_price || itemCustomPrice) && itemPriceDivided"
        class="flex flex-wrap items-center justify-center gap-x-2"
      >
        <span class="text-xl font-bold @xl:text-3xl">{{ itemPriceFormatted }}</span>
        <span class="text-xs font-normal text-gray-500">/{{ t('month-abbr') }}</span>
      </div>

      <div v-if="isPriceListV1_1">
        <span class="text-xs font-normal text-gray-500">
          {{ paymentPeriodUnit === 'year' ? t('subscribe.plans.table.commitAnnually') : t('subscribe.plans.table.cancelAnytime') }}
        </span>
      </div>

      <div class="mb-0 mt-3 w-full space-y-3">
        <DTButton
          v-if="itemPrice"
          variant="blue"
          class="w-full"
          size="sm"
          @click="() => {
            selectedPlan = item
            registerPlanSelectedEvent()
          }"
        >
          {{ t('choose') }}
        </DTButton>
        <DTButton
          v-else
          size="sm"
          class="w-full"
          @click="emit('contactRequested')"
        >
          {{ t('contact-us') }}
        </DTButton>
      </div>
    </div>
  </th>
</template>
